import {
  Modal,
  Form,
  Input,
  Select,
  notification,
  Switch,
  DatePicker,
} from "antd";
import {
  MachineItem,
  ProductCardItem,
  TASK_TRIGGER_ON_EVENT,
  TASK_TRIGGER_PERIODICALLY,
  useCreateTaskMutation,
  useGetAllProductCardsQuery,
  useUploadFileMutation,
  useUploadImageMutation,
} from "shared/api";
import { useEffect, useRef, useState } from "react";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useTranslation } from "react-i18next";
import {
  TaskIconUploader,
  PeriodicitySection,
  AddAssociationsSection,
} from "./components";

dayjs.extend(utc);
dayjs.extend(timezone);

interface AddTaskModalProps {
  open: boolean;
  onClose: () => void;
  transformedUsers: {
    label: string;
    value: number | null | undefined;
  }[];
  machinesData: MachineItem[] | undefined;
  refetchExecutions: () => void | undefined;
  suppliersCompaniesFormatted:
    | {
        label: string;
        value: number;
      }[]
    | undefined;
}

export const AddTaskModal: React.FC<AddTaskModalProps> = ({
  open,
  onClose,
  transformedUsers,
  machinesData,
  refetchExecutions,
  suppliersCompaniesFormatted,
}) => {
  const [form] = Form.useForm();
  const { t } = useTranslation();
  const [api, contextHolder] = notification.useNotification();
  const fileDocRef = useRef<HTMLInputElement>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [uploadedIconFile, setUploadedIconFile] = useState<File | null>(null);
  const [showDescription, setShowDescription] = useState(false);
  const [attachedFiles, setAttachedFiles] = useState<File[]>([]);

  const isRecurrent = Form.useWatch("is_recurrent", form);
  const enabled = Form.useWatch("enabled", form);
  const trigger = Form.useWatch("trigger", form);

  const isPeriodicTrigger = trigger === TASK_TRIGGER_PERIODICALLY;

  useEffect(() => {
    if (trigger === TASK_TRIGGER_PERIODICALLY) {
      form.setFieldsValue({
        is_recurrent: true,
        machines: [],
        product_cards: [],
        companies: [],
      });
    }
  }, [trigger, form]);

  const [uploadFile] = useUploadFileMutation();
  const [uploadImage] = useUploadImageMutation();
  const [createTask, { isLoading }] = useCreateTaskMutation();

  const { data: productCardsData } = useGetAllProductCardsQuery({}) as {
    data: ProductCardItem[] | undefined;
  };

  useEffect(() => {
    if (!open) {
      form.resetFields();
      setFileName(null);
      setPreviewUrl(null);
      setUploadedIconFile(null);
      setAttachedFiles([]);
    }
  }, [open, form]);

  const handleAttachFiles = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { files } = e.target;
    if (!files) return;
    setAttachedFiles((prev) => [...prev, ...Array.from(files)]);
  };

  const handleFinish = async (values: any) => {
    try {
      const payload = {
        name: values.name,
        trigger: values.trigger ?? null,
        periodicity: values.periodicity || null,
        periodicity_multiple: values.periodicity_multiple ?? null,
        is_recurrent: values.is_recurrent || false,
        start_periodicity: values.start_periodicity
          ? dayjs(values.start_periodicity)
              .utc()
              .format("YYYY-MM-DDTHH:mm:ss[Z]")
          : null,
        machines:
          values.trigger === TASK_TRIGGER_PERIODICALLY
            ? []
            : values.machines || [],
        product_cards:
          values.trigger === TASK_TRIGGER_PERIODICALLY
            ? []
            : values.product_cards || [],
        enabled: values.enabled,
        priority: values.priority,
        users: values.user || [],
        description: showDescription ? values.description : null,
        companies:
          values.trigger === TASK_TRIGGER_PERIODICALLY ? [] : values.companies,
      };

      const { id: taskId } = await createTask(payload).unwrap();

      if (uploadedIconFile && taskId) {
        await uploadImage({ id: taskId, file: uploadedIconFile }).unwrap();
      }

      if (attachedFiles.length > 0 && taskId) {
        await Promise.all(
          attachedFiles.map((file) => uploadFile({ id: taskId, file }).unwrap())
        );
      }

      refetchExecutions();

      api.success({
        message: "Task Created",
        description: "The task was successfully created.",
        placement: "topRight",
      });

      setShowDescription(false);
      setAttachedFiles([]);
      onClose();
    } catch (err) {
      api.error({
        message: "Task Creation Failed",
        description: "Something went wrong while creating the task.",
        placement: "topRight",
      });

      // eslint-disable-next-line no-console
      console.error("Failed to create task", err);
    } finally {
      onClose();
    }
  };

  const handleIconUpload = (file: File) => {
    const reader = new FileReader();
    reader.onload = () => {
      const base64 = reader.result as string;
      setPreviewUrl(base64);
      setUploadedIconFile(file);
      setFileName(file.name);
    };
    reader.readAsDataURL(file);
  };

  const handleIconRemove = () => {
    setPreviewUrl(null);
    setUploadedIconFile(null);
    setFileName(null);
  };

  const handleRemoveFile = (index: number) => {
    setAttachedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const themeColor = JSON.parse(
    localStorage.getItem("color-theme") || '"#002F5F"'
  );

  return (
    <>
      {contextHolder}
      <Modal
        open={open}
        onCancel={onClose}
        onOk={() => form.submit()}
        confirmLoading={isLoading}
        destroyOnClose
        title={
          <div className="flex items-center justify-between w-[95%] mb-3">
            <span>{t("taskHub.modal.addTask") || ""}</span>
            <div className="flex gap-2 items-center min-w-[130px] justify-end">
              <span className="text-sm">
                {enabled ? "Enabled" : "Disabled"}
              </span>
              <Switch
                checked={enabled}
                onChange={(checked) => form.setFieldValue("enabled", checked)}
                style={{
                  backgroundColor: enabled ? "limegreen" : "tomato",
                }}
              />
            </div>
          </div>
        }
        width={720}
        okButtonProps={{
          style: {
            backgroundColor: themeColor,
            color: "#fff",
            borderColor: "#1677ff",
          },
        }}
        okText={t("taskHub.modal.ok") || ""}
        cancelText={t("taskHub.modal.cancel") || ""}
      >
        <Form
          initialValues={{
            enabled: true,
            trigger: TASK_TRIGGER_PERIODICALLY,
          }}
          form={form}
          layout="vertical"
          onFinish={handleFinish}
        >
          <div>
            <div className="grid grid-cols-2 gap-x-4">
              <div className="col-span-2 flex items-center justify-between">
                <h3 className="text-base font-semibold mb-2">
                  {t("taskHub.modal.taskIcon") || ""}
                </h3>
                <TaskIconUploader
                  previewUrl={previewUrl}
                  fileName={fileName}
                  uploadedIconFile={uploadedIconFile}
                  onUpload={handleIconUpload}
                  onRemove={handleIconRemove}
                />
              </div>

              <Form.Item
                name="name"
                label={t("taskHub.modal.name") || ""}
                rules={[{ required: true }]}
              >
                <Input placeholder={t("taskHub.modal.enterTaskName") || ""} />
              </Form.Item>

              <Form.Item name="user" label={t("taskHub.modal.user") || ""}>
                <Select
                  mode="multiple"
                  showSearch
                  options={transformedUsers}
                  optionFilterProp="label"
                  placeholder={t("taskHub.modal.selectUser") || ""}
                  filterOption={(input, option) =>
                    (option?.label as string)
                      .toLowerCase()
                      .includes(input.toLowerCase())
                  }
                />
              </Form.Item>

              <Form.Item
                name="priority"
                label={t("taskHub.modal.priority") || ""}
              >
                <Select placeholder={t("taskHub.modal.selectPriority") || ""}>
                  <Select.Option value={1}>1</Select.Option>
                  <Select.Option value={2}>2</Select.Option>
                  <Select.Option value={3}>3</Select.Option>
                  <Select.Option value={4}>4</Select.Option>
                  <Select.Option value={5}>5</Select.Option>
                </Select>
              </Form.Item>

              <Form.Item
                name="start_periodicity"
                label={t("taskHub.modal.startDate") || "Start date"}
              >
                <DatePicker
                  format="YYYY-MM-DD HH:mm"
                  showTime
                  style={{ width: "100%" }}
                  getPopupContainer={(n) =>
                    n.closest(".ant-modal-wrap") ?? document.body
                  }
                  disabledDate={(date) => date && date.isBefore(dayjs(), "day")}
                />
              </Form.Item>

              <Form.Item
                name="trigger"
                label={t("taskHub.modal.taskTrigger") || ""}
                rules={[{ required: true }]}
                className="col-span-2"
              >
                <Select
                  placeholder={t("taskHub.modal.selectTaskTrigger") || ""}
                  options={[
                    {
                      value: TASK_TRIGGER_PERIODICALLY,
                      label:
                        t("taskHub.modal.triggerPeriodically") ||
                        "Trigger periodically",
                    },
                    {
                      value: TASK_TRIGGER_ON_EVENT,
                      label:
                        t("taskHub.modal.triggerOnEvent") || "Trigger on event",
                    },
                  ]}
                />
              </Form.Item>
            </div>
          </div>

          <PeriodicitySection
            isRecurrent={isRecurrent}
            recurrenceCheckboxDisabled={isPeriodicTrigger}
          />

          <AddAssociationsSection
            machinesData={machinesData}
            productCardsData={productCardsData}
            suppliersCompaniesFormatted={suppliersCompaniesFormatted}
            showDescription={showDescription}
            attachedFiles={attachedFiles}
            themeColor={themeColor}
            fileDocRef={fileDocRef}
            onToggleDescription={() => setShowDescription((prev) => !prev)}
            onAttachFiles={handleAttachFiles}
            onRemoveFile={handleRemoveFile}
            showEntityAssociations={!isPeriodicTrigger}
            associationsIntroText={
              !isPeriodicTrigger
                ? String(t("taskHub.modal.associationsOrderIntro"))
                : undefined
            }
          />

          <Form.Item name="enabled" noStyle>
            <Input type="hidden" />
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};
